import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Backdrop,
  Button,
  FormFieldSet,
  InfoCardNewAdv,
  InfoText,
  InfoTimeCard,
  InfoTimeCardAdv,
  KeyValue,
  LogoLoader,
  NewTable,
  NewTableAdv,
  PageHeader,
  PageTitle,
  showToast,
  ToolTip,
  useFormHandler,
} from "../../../../../../../../components";
import {
  dateFormator,
  formatNumber,
  mapTableRows,
} from "../../../../../../../../utils";
import TimesheetDetails from "./Modals/TimesheetDetails";
import UnpaidTimesheetsTwo from "./Modals/UnpaidTimesheetsTwo";
import { useDispatch, useSelector } from "react-redux";
import {
  clearTimesheetData,
  getTimesheetsStart,
} from "../../../../../../../../redux/slices/timesheets/timsheetSlice";
import { formatCurrency } from "../../../../../../../../utils/stringUtils";
import { setCurrentPayrollEditing } from "../../../../../../../../redux/slices/payroll/payrollSlice";
import { ENDPOINTS } from "../../../../../../../../redux/apiConfig";
import axiosRequest from "../../../../../../../../redux/slices/universal/axiosService";
import dayjs from "dayjs";
import { calculateTotalAmount } from "../ProposedEmployeesDetails";
import { exactUTCDate } from "../../../../../../../../utils/dateFormator";

const Timesheets = ({ rowData, disableEditing = false, tab = null }) => {
  // console.log("rowData in timesheets", rowData, disableEditing);
  const dispatch = useDispatch();
  const { currentPayrollEditing, loading: payrollLoading } =
    useSelector((state) => state.payRoll) || {};
  const { loading, error, timesheets, totalCount } = useSelector(
    (state) => state.timesheet,
  );
  const [unpaidTimesheetDetailsData, setUnpaidTimesheetDetailsData] = useState(
    [],
  );
  const timesheetData = timesheets?.data || [];
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      setMounted((prev) => !prev);
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  const [unpaidTimesheetDetails, setUnpaidTimesheetDetails] = useState(false);
  const [unpaidIds, setUnpaidIds] = useState([]);
  const [currentSelectedIds, setCurrentSelectedIds] = useState([]);
  const [selectedUnpaidTimesheetDetails, setSelectedUnpaidTimesheetDetails] =
    useState([]);
  const [timesheetDetailsData, setTimesheetDetailsData] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [totalPayoutAmount, setTotalPayoutAmount] = useState(0.0);
  const [showUnpaid, setShowUnpaid] = useState(false);

  // ========== HELPER: Convert date from YYYYMMDD to dayjs ==========
  const parseDateEntry = useCallback((dateNum) => {
    const dateStr = String(dateNum);
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return dayjs(`${year}-${month}-${day}`).startOf("day");
  }, []);

  // ========== HELPER: Get all payment rates sorted by effective date ==========
  const getAllPaymentRates = useCallback((row) => {
    const payments = row?.placementId?.payments || [];
    return payments
      .map((p) => ({
        rate: p?.payRate?.payRatePerHour || 0,
        payType: p?.payRate?.payType,
        effectiveDate: dayjs(exactUTCDate(p?.payRate?.effectiveDate)).startOf(
          "day",
        ),
      }))
      .sort((a, b) => a.effectiveDate.valueOf() - b.effectiveDate.valueOf());
  }, []);

  // ========== HELPER: Get rate applicable for a specific date ==========
  const getRateForDate = useCallback((allRates, entryDate) => {
    if (!allRates.length) return { rate: 0, payType: 1 };

    const match = [...allRates]
      .reverse()
      .find((p) => entryDate.isSameOrAfter(p.effectiveDate));

    return match || allRates[0] || { rate: 0, payType: 1 };
  }, []);

  // ========== HELPER: Calculate detailed breakdown per rate ==========
  const calculateRowDetailsWithRates = useCallback(
    (row) => {
      const allRates = getAllPaymentRates(row);
      const rateBreakdown = {};
      let totalAmount = 0;
      let totalHours = 0;

      (row?.dailyEntries || []).forEach((entry) => {
        const entryDate = parseDateEntry(entry.date);
        const { rate, payType } = getRateForDate(allRates, entryDate);
        const hours = entry.st || 0;
        const amount = payType === 1 ? hours * rate : rate;

        totalHours += hours;
        totalAmount += amount;

        const rateKey = `${rate}_${payType}`;
        if (!rateBreakdown[rateKey]) {
          rateBreakdown[rateKey] = {
            rate,
            payType,
            hours: 0,
            amount: 0,
            effectiveDate: getRateForDate(allRates, entryDate).effectiveDate,
          };
        }
        rateBreakdown[rateKey].hours += hours;
        rateBreakdown[rateKey].amount += amount;
      });

      return {
        totalAmount,
        totalHours,
        rateBreakdown: Object.values(rateBreakdown).sort(
          (a, b) => a.effectiveDate.valueOf() - b.effectiveDate.valueOf(),
        ),
        hasMultipleRates: Object.keys(rateBreakdown).length > 1,
      };
    },
    [getAllPaymentRates, getRateForDate, parseDateEntry],
  );

  // ========== HELPER: Calculate Total Amount for Rows ==========
  const calculateRowTotal = useCallback(
    (rows) => {
      return rows?.reduce((acc, row) => {
        const { totalAmount } = calculateRowDetailsWithRates(row);
        return acc + totalAmount;
      }, 0);
    },
    [calculateRowDetailsWithRates],
  );

  const calculateRowBalance = useCallback(
    (rows) => {
      const totalAmount = rows?.reduce((acc, row) => {
        const { totalAmount } = calculateRowDetailsWithRates(row);
        return acc + totalAmount;
      }, 0);
      const totalPaidAmount = rows?.reduce((acc, row) => {
        return acc + row.amounts.paid;
      }, 0);
      return totalAmount - totalPaidAmount;
    },
    [calculateRowDetailsWithRates],
  );

  // ========== HELPER: Calculate Total Hours ==========
  const calculateTotalHours = useCallback(
    (rows) => {
      return rows?.reduce((acc, row) => {
        const { totalHours } = calculateRowDetailsWithRates(row);
        return acc + Number(totalHours.toFixed(2));
      }, 0);
    },
    [calculateRowDetailsWithRates],
  );

  useEffect(() => {
    let employeeIdToCall =
      rowData?.employeeId?.employmentType === 4
        ? rowData?.employeeId?.linkedTo
        : rowData?.employeeId?.uiid;

    dispatch(
      getTimesheetsStart({
        filters: {
          status: { type: "IN", value: [2, 3, 6] },
          // placementId: { type: "NONE", value: rowData?.jobId },
          employeeId: { type: "NONE", value: employeeIdToCall },
          period: {
            type: "RANGE",
            value: { from: rowData?.payCycle?.from, to: rowData?.payCycle?.to },
          },
        },
        lim: 100,
        sort: { updatedAt: -1 },
        ref: "Employee Client Placement",
      }),
    );

    const params = {
      coll: "Timesheet",
      lim: 1000,
      fil: JSON.stringify({
        ...(!tab && { paid: { type: "NONE", value: false } }),
        status: { type: "IN", value: [2, 3, 6] },
        // status: { type: "IN", value: [2, 3] },
        // placementId: { type: "NONE", value: rowData?.jobId },
        employeeId: { type: "NONE", value: employeeIdToCall },
        "period.from": { type: "LT", value: rowData?.payCycle?.from },
        "period.to": { type: "LT", value: rowData?.payCycle?.from },
      }),
      cache: false,
      sort: JSON.stringify({ updatedAt: -1 }),
      ref: "Employee Client Placement",
    };

    axiosRequest({
      method: "GET",
      url: ENDPOINTS.TIMESHEETS.GET_ALL,
      params,
    }).then((res) => {
      setUnpaidTimesheetDetailsData(res?.data?.data || []);
    });

    return () => {
      dispatch(clearTimesheetData());
      setUnpaidTimesheetDetailsData([]);
    };
  }, [rowData]);

  useEffect(() => {
    if (!currentPayrollEditing?.tabswitched) {
      const dbtimesheetIds = [
        ...(rowData?.timesheets?.ids?.map((row) => row) || []),
        ...(rowData?.timesheets?.unpaidIds?.map((row) => row) || []),
      ];
      setCurrentSelectedIds(dbtimesheetIds);
      console.log({ rowData });
      const unpaidFiltered = unpaidTimesheetDetailsData
        .filter((item) => dbtimesheetIds.includes(item?.uiid))
        .map((ts) => ({
          ...ts,
          amounts: {
            ...ts.amounts,
            deduction:
              ts.amounts?.adjustment?.find((a) => a?.payrollId === rowData.id)
                ?.amount ?? 0,
          },
        }));

      const paidFiltered = timesheets?.data
        ?.filter((item) => dbtimesheetIds.includes(item?.uiid))
        ?.map((row) => {
          const { totalAmount, rateBreakdown, hasMultipleRates } =
            calculateRowDetailsWithRates(row);
          console.log({ totalAmount, rateBreakdown, hasMultipleRates });
          return {
            ...row,
            amounts: {
              ...row.amounts,
              deduction: Number(totalAmount) ? totalAmount : 0,
              rateBreakdown,
            },
          };
        });

      const totalPayAmountUnpaid = !tab
        ? calculateRowBalance(unpaidFiltered)
        : calculateRowTotal(unpaidFiltered);
      console.log("totalPayAmountUnpaid", totalPayAmountUnpaid, unpaidFiltered);
      const totalUnpaidHoursCalculated = calculateTotalHours(unpaidFiltered);
      const totalPayAmount = calculateRowTotal(paidFiltered);
      const total = calculateTotalHours(paidFiltered);

      // console.log("totalPayAmount changed to 1", totalPayAmount, total);

      setUnpaidIds(unpaidFiltered.map((row) => row?.uiid) || []);
      console.log("trigger number 1");
      dispatch(
        setCurrentPayrollEditing({
          edited: true,
          unpaidtimesheets: unpaidFiltered || [],
          timesheets: paidFiltered || [],
          totalUnpaidHours: totalUnpaidHoursCalculated,
          totalUnpaidPayAmount: totalPayAmountUnpaid,
          currentTimesheetHours: total,
          currentTimesheetPayAmount: totalPayAmount,
          totalTimesheetHours: totalUnpaidHoursCalculated + total || 0,
          totalTimesheetPayAmount:
            (currentPayrollEditing?.modifications?.amt
              ? currentPayrollEditing?.modifications?.amt
              : currentPayrollEditing?.modifications?.amt === 0
                ? 0
                : totalPayAmountUnpaid) + totalPayAmount || 0,
        }),
      );
    } else {
      const totalPayAmountUnpaid = calculateRowBalance(
        currentPayrollEditing?.unpaidtimesheets || [],
      );
      const totalUnpaidHoursCalculated = calculateTotalHours(
        currentPayrollEditing?.unpaidtimesheets || [],
      );
      const totalPayAmount = calculateRowTotal(
        currentPayrollEditing?.timesheets || [],
      );
      const total = calculateTotalHours(
        currentPayrollEditing?.timesheets || [],
      );

      // console.log("totalPayAmount changed to 2", totalPayAmount);

      setUnpaidIds(
        currentPayrollEditing?.unpaidtimesheets?.map((row) => row?.uiid) || [],
      );
      console.log("trigger number 2");
      dispatch(
        setCurrentPayrollEditing({
          edited: true,
          unpaidtimesheets: currentPayrollEditing?.unpaidtimesheets || [],
          timesheets:
            currentPayrollEditing?.timesheets?.map((row) => {
              const { totalAmount, rateBreakdown, hasMultipleRates } =
                calculateRowDetailsWithRates(row);
              console.log({ totalAmount, rateBreakdown, hasMultipleRates });
              return {
                ...row,
                amounts: {
                  ...row.amounts,
                  deduction: Number(totalAmount) ? totalAmount : 0,
                  rateBreakdown,
                },
              };
            }) || [],
          totalUnpaidHours: totalUnpaidHoursCalculated,
          totalUnpaidPayAmount: totalPayAmountUnpaid,
          currentTimesheetHours: total,
          currentTimesheetPayAmount: totalPayAmount,
          totalTimesheetHours: totalUnpaidHoursCalculated + total || 0,
          totalTimesheetPayAmount:
            (currentPayrollEditing?.modifications?.amt
              ? currentPayrollEditing?.modifications?.amt
              : currentPayrollEditing?.modifications?.amt === 0
                ? 0
                : totalPayAmountUnpaid) + totalPayAmount || 0,
        }),
      );
    }
  }, [
    rowData,
    unpaidTimesheetDetailsData,
    timesheets,
    calculateRowTotal,
    calculateTotalHours,
  ]);

  useEffect(() => {
    if (
      unpaidIds?.length > 0 ||
      currentPayrollEditing?.unpaidtimesheets?.length > 0
    ) {
      setShowUnpaid(true);
    } else {
      setShowUnpaid(false);
    }

    const idsCollected = unpaidIds.map((row) => row);
    const filteredData = [...unpaidTimesheetDetailsData]
      .filter((item) => idsCollected.includes(String(item.uiid)))
      ?.map((row) => {
        const { totalAmount, rateBreakdown, hasMultipleRates } =
          calculateRowDetailsWithRates(row);
        console.log({ totalAmount, rateBreakdown, hasMultipleRates });
        return {
          ...row,
          amounts: {
            ...row.amounts,
            deduction: Number(totalAmount) ? totalAmount : 0,
            rateBreakdown,
          },
        };
      });

    const reduxArray = currentPayrollEditing?.unpaidtimesheets;
    const combinedArray = [
      ...new Map(
        [...(reduxArray || []), ...filteredData].map((item) => [
          item.uiid,
          item,
        ]),
      ).values(),
    ];

    setSelectedUnpaidTimesheetDetails(combinedArray);

    const totalPayAmount = !tab
      ? calculateRowBalance(combinedArray)
      : calculateRowTotal(combinedArray);
    const totalUnpaidHoursCalculated = calculateTotalHours(combinedArray);

    // console.log("totalPayAmount changed to 3", totalPayAmount);
    console.log("trigger number 3");
    dispatch(
      setCurrentPayrollEditing({
        unpaidtimesheets: combinedArray || [],
        totalTimesheetHours:
          (currentPayrollEditing?.currentTimesheetHours || 0) +
          totalUnpaidHoursCalculated,
        totalTimesheetPayAmount:
          (currentPayrollEditing?.currentTimesheetPayAmount || 0) +
          (currentPayrollEditing?.modifications?.amt
            ? currentPayrollEditing?.modifications?.amt
            : currentPayrollEditing?.modifications?.amt === 0
              ? 0
              : totalPayAmount),
        totalUnpaidHours: totalUnpaidHoursCalculated,
        totalUnpaidPayAmount: totalPayAmount,
        currentTimesheetHours:
          currentPayrollEditing?.currentTimesheetHours || 0,
        currentTimesheetPayAmount:
          currentPayrollEditing?.currentTimesheetPayAmount || 0,
      }),
    );
  }, [unpaidIds, calculateRowTotal, calculateTotalHours]);

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler({
      radioSelection: "",
      enterValue: "",
      matchLca: "",
    });

  // ========== COLUMNS FOR PAID TIMESHEETS ==========
  const columns = [
    {
      key: "jobTitle",
      label: "Job Title",
      render: (row) => {
        const isInredux = currentPayrollEditing?.timesheets?.find(
          (item) => item?.uiid === row?.uiid,
        );
        return (
          <div className={`cursor-pointer ${isInredux ? "text-linkblue" : ""}`}>
            {row?.placementId?.jobInfo?.jobTitle || "-"}
          </div>
        );
      },
    },
    {
      key: "placementType",
      label: "Placement Type",
      render: (row) => {
        return (
          <div className={``}>
            {row?.placementId?.clientPlacement?.placement || "-"}
          </div>
        );
      },
    },
    {
      key: "client",
      label: "Client",
      render: (row) => <div>{row?.clientId?.businessName || "-"}</div>,
    },
    {
      key: "payCycle",
      label: "Pay Cycle",
      render: (row) => (
        <div>
          {row?.period?.from && row?.period?.to
            ? `${exactUTCDate(row.period.from)} - ${exactUTCDate(row.period.to)}`
            : "-"}
        </div>
      ),
    },
    {
      key: "classification",
      label: "Classification",
      render: () => <div>Standard Time</div>,
    },
    {
      key: "totalHours",
      label: "Total Hours",
      render: (row) => {
        try {
          const { totalHours } = calculateRowDetailsWithRates(row);
          return <div>{totalHours.toFixed(2)}</div>;
        } catch {
          return <div>-</div>;
        }
      },
    },
    {
      key: "payRate",
      label: "Pay Rate",
      render: (row) => {
        try {
          const { rateBreakdown, hasMultipleRates } =
            calculateRowDetailsWithRates(row);
          if (hasMultipleRates) {
            return (
              <div className="flex flex-col gap-1">
                {rateBreakdown.map((rb, idx) => (
                  <div key={idx} className="text-xs">
                    {formatCurrency(rb.rate)}{" "}
                    <span className="text-gray-500">
                      ({rb.hours.toFixed(2)}h)
                    </span>
                  </div>
                ))}
              </div>
            );
          }
          return <div>{formatCurrency(rateBreakdown[0]?.rate || 0)}</div>;
        } catch {
          return <div>-</div>;
        }
      },
    },
    {
      key: "total",
      label: "Total",
      render: (row) => {
        try {
          const { totalAmount, rateBreakdown, hasMultipleRates } =
            calculateRowDetailsWithRates(row);
          if (hasMultipleRates) {
            return (
              <div className="flex flex-col gap-1">
                {rateBreakdown.map((rb, idx) => (
                  <div key={idx} className="text-xs">
                    {formatCurrency(rb.amount)}
                  </div>
                ))}
                <div className="font-semibold border-t pt-1">
                  {formatCurrency(totalAmount)}
                </div>
              </div>
            );
          }
          return <div>{formatCurrency(totalAmount)}</div>;
        } catch {
          return <div>-</div>;
        }
      },
    },
  ];

  const alteredTimesheetData =
    timesheetData.map((item) => ({
      ...item,
      id: item.uiid,
    })) || [];

  const mappedRows = mapTableRows(alteredTimesheetData, columns);

  // ========== COLUMNS FOR UNPAID TIMESHEETS ==========
  const columnsTwo = [
    {
      key: "jobTitle",
      label: "Job Title",
      // width: "180px",
      render: (item) => (
        <div>{item?.placementId?.jobInfo?.jobTitle || "-"}</div>
      ),
    },
    {
      key: "placementType",
      label: "PlacementType",
      // width: "180px",
      render: (item) => (
        <div>{item?.placementId?.clientPlacement?.placement || "-"}</div>
      ),
    },
    {
      key: "client",
      label: "Client",
      // width: "130px",
      render: (item) => <div>{item?.clientId?.businessName || "-"}</div>,
    },
    {
      key: "payCycle",
      label: "Pay Cycle",
      // width: "180px",
      render: (item) => (
        <div>
          {item?.period?.from && item?.period?.to
            ? `${exactUTCDate(item?.period?.from)} - ${exactUTCDate(
                item?.period?.to,
              )}`
            : "-"}
        </div>
      ),
    },
    {
      key: "classification",
      label: "Classification",
      // width: "180px",
      render: (item) => <div>{"Standard Time"}</div>,
    },
    {
      key: "totalHours",
      label: "Total Hours",
      // width: "180px",
      render: (item) => {
        const { totalHours } = calculateRowDetailsWithRates(item);
        return <div>{totalHours.toFixed(2)}</div>;
      },
    },
    {
      key: "usPayRate",
      label: "US Pay Rate",
      // width: "180px",
      render: (row) => {
        const { rateBreakdown, hasMultipleRates } =
          calculateRowDetailsWithRates(row);
        const lastPayment =
          row?.placementId?.payments?.[row?.placementId?.payments?.length - 1];
        const subPercent = lastPayment?.payRate?.subPercent || 0;

        if (hasMultipleRates) {
          return (
            <div className="flex flex-col gap-1">
              {rateBreakdown.map((rb, idx) => {
                const finalRate =
                  subPercent > 0 ? (rb.rate * 100) / subPercent : rb.rate;
                return (
                  <div key={idx} className="text-xs">
                    {isNaN(finalRate) ? 0 : finalRate?.toFixed(2) || 0}
                  </div>
                );
              })}
            </div>
          );
        }

        const rate = rateBreakdown[0]?.rate || 0;
        const finalRate = subPercent > 0 ? (rate * 100) / subPercent : rate;
        return <div>{isNaN(finalRate) ? 0 : finalRate?.toFixed(2) || 0}</div>;
      },
    },
    {
      key: "indiaPayRate",
      label: "India Pay Rate",
      // width: "180px",
      render: (row) => {
        const lastPayment =
          row?.placementId?.payments?.[row?.placementId?.payments?.length - 1];
        const subPercent = lastPayment?.payRate?.subPercent;
        const { rateBreakdown, hasMultipleRates } =
          calculateRowDetailsWithRates(row);

        if (!subPercent) return <div>-</div>;

        if (hasMultipleRates) {
          return (
            <div className="flex flex-col gap-1">
              {rateBreakdown.map((rb, idx) => (
                <div key={idx} className="text-xs">
                  {rb.rate.toFixed(2)}
                </div>
              ))}
            </div>
          );
        }

        return <div>{rateBreakdown[0]?.rate?.toFixed(2) || "-"}</div>;
      },
    },
    {
      key: "totalAmount",
      label: "Total Amount",
      // width: "150px",
      render: (item) => {
        try {
          const { totalAmount } = calculateRowDetailsWithRates(item);
          return <div>{totalAmount.toFixed(2)}</div>;
        } catch (e) {
          return <div>Error: {e.message}</div>;
        }
      },
    },
    {
      key: "balancedAmount",
      label: "Balanced Amount",
      // width: "150px",
      render: (item) => {
        try {
          const { totalAmount } = calculateRowDetailsWithRates(item);
          return (
            <div>
              {Number(
                totalAmount.toFixed(2) - (item?.amounts?.paid || 0).toFixed(2),
              ).toFixed(2)}
            </div>
          );
        } catch (e) {
          return <div>Error: {e.message}</div>;
        }
      },
    },
    {
      key: "adjustedTotal",
      label: "Adjusted Total",
      render: (row) => (
        <div>
          {formatCurrency(
            row?.amounts?.adjustment?.find(
              (item) => item?.payrollId === rowData?.uiid,
            )?.amount || 0,
          )}
        </div>
      ),
    },
    {
      key: "externalAdjustedTotal",
      label: "External Adjusted Total",
      render: (row) => (
        <div>
          {formatCurrency(
            row?.amounts?.adjustment
              ?.filter((item) => item?.payrollId !== rowData?.uiid)
              ?.reduce((acc, curr) => acc + curr.amount, 0) || 0,
          )}
        </div>
      ),
    },
  ];

  // ========== HANDLE SELECTION CHANGE FOR PAID TIMESHEETS ==========
  const handleSelectionChange = (selectedRows) => {
    const ids = selectedRows.map((r) => r);
    const filtered = timesheetData
      .filter((i) => ids.includes(String(i.uiid)))
      ?.map((row) => {
        const { totalAmount, rateBreakdown, hasMultipleRates } =
          calculateRowDetailsWithRates(row);
        console.log({ totalAmount, rateBreakdown, hasMultipleRates });
        return {
          ...row,
          amounts: {
            ...row.amounts,
            deduction: Number(totalAmount) ? totalAmount : 0,
            rateBreakdown,
          },
        };
      });
    setTimesheetDetailsData(filtered || []);

    const totalHours = calculateTotalHours(filtered);
    console.log({ filtered });
    const totalPayAmount = calculateRowTotal(filtered);

    console.log("totalPayAmount changed to 4", totalPayAmount);

    // handleChange("radioSelection", "");
    // handleChange("enterValue", "");
    // handleChange("matchLca", "");
    console.log("trigger number 4");
    dispatch(
      setCurrentPayrollEditing({
        ...currentPayrollEditing,
        timesheets: filtered,
        totalTimesheetHours:
          (currentPayrollEditing?.totalUnpaidHours || 0) + totalHours,
        totalTimesheetPayAmount:
          (currentPayrollEditing?.modifications?.amt ??
            currentPayrollEditing?.totalUnpaidPayAmount ??
            0) + totalPayAmount,
        currentTimesheetHours: totalHours,
        currentTimesheetPayAmount: totalPayAmount,
        totalUnpaidHours: currentPayrollEditing?.totalUnpaidHours || 0,
        totalUnpaidPayAmount: currentPayrollEditing?.totalUnpaidPayAmount || 0,
        // modifications: { type: null, amt: null },
      }),
    );
  };

  // ========== HANDLE SELECTION CHANGE FOR UNPAID TIMESHEETS ==========
  const handleSelectionChangeUnpaid = (selectedRows) => {
    const idsCollected = selectedRows.map((row) => row);
    const filteredData = [...unpaidTimesheetDetailsData]
      .filter((item) => idsCollected.includes(String(item.uiid)))
      ?.map((row) => {
        // console.log({handleSelectionChangeUnpaid:row})
        const { totalAmount, rateBreakdown, hasMultipleRates } =
          calculateRowDetailsWithRates(row);
        console.log({ totalAmount, rateBreakdown, hasMultipleRates });
        return {
          ...row,
          amounts: {
            ...row.amounts,
            deduction: Number(totalAmount) ? totalAmount : 0,
            rateBreakdown,
          },
        };
      });
    console.log({ filteredData });
    setUnpaidIds(idsCollected);
    setSelectedUnpaidTimesheetDetails(filteredData || []);

    const total = calculateTotalHours(filteredData);
    const totalPayAmount = calculateRowTotal(filteredData);

    console.log("totalPayAmount changed to 5", totalPayAmount, filteredData);

    dispatch(
      setCurrentPayrollEditing({
        unpaidtimesheets: filteredData || [],
        totalUnpaidTimesheetHours: total,
        totalUnpaidTimesheetPayAmount: totalPayAmount,
        currentTimesheetHours:
          currentPayrollEditing?.currentTimesheetHours || 0,
        currentTimesheetPayAmount:
          currentPayrollEditing?.currentTimesheetPayAmount || 0,
        totalTimesheetHours:
          (total || 0) + (currentPayrollEditing?.totalTimesheetHours || 0),
        totalTimesheetPayAmount:
          (currentPayrollEditing?.modifications?.amt
            ? currentPayrollEditing?.modifications?.amt
            : currentPayrollEditing?.modifications?.amt === 0
              ? 0
              : totalPayAmount || 0) +
          (currentPayrollEditing?.totalTimesheetPayAmount || 0),
        modifications: { type: null, amt: null },
      }),
    );
  };

  const currentIds = useMemo(
    () => currentPayrollEditing?.timesheets?.map((d) => d.uiid) || [],
    [currentPayrollEditing],
  );

  const currentUnpaidIds = useMemo(
    () => currentPayrollEditing?.unpaidtimesheets?.map((d) => d.uiid) || [],
    [currentPayrollEditing],
  );

  const updatedCurrentIds = useMemo(
    () => [...new Set(currentIds)],
    [currentIds],
  );

  const updatedUnpaidIds = useMemo(
    () => [...new Set(currentUnpaidIds)],
    [currentUnpaidIds],
  );

  const hasUnpaidSelected = useMemo(
    () =>
      !!unpaidTimesheetDetailsData?.some((item) =>
        currentUnpaidIds.includes(item.uiid),
      ),
    [unpaidTimesheetDetailsData, currentUnpaidIds],
  );

  const unpaidSelectedData =
    unpaidTimesheetDetailsData
      ?.map((item) => ({ ...item, id: item.uiid }))
      .filter((uiid) => updatedUnpaidIds.includes(uiid.uiid)) || [];

  const mappedRowsTwo = mapTableRows(unpaidSelectedData, columnsTwo);

  useEffect(() => {
    if (formData.radioSelection) {
      if (formData.radioSelection == "1") {
        const value = Number(formData?.enterValue || 0);
        let unpaidTimesheetsUpdateForAdjustments =
          currentPayrollEditing?.unpaidtimesheets || [];

        try {
          let deduction = value;
          unpaidTimesheetsUpdateForAdjustments =
            currentPayrollEditing?.unpaidtimesheets
              ? [...currentPayrollEditing.unpaidtimesheets]
                  .sort((a, b) => {
                    const totalA = Number(a.amounts?.total || 0);
                    const totalB = Number(b.amounts?.total || 0);
                    if (totalA !== totalB) return totalA - totalB;
                    return Number(a.uiid) - Number(b.uiid);
                  })
                  .map((ts) => {
                    let available = Number(ts.amounts?.total || 0);
                    let used = Math.min(available, deduction);
                    deduction -= used;
                    return {
                      ...ts,
                      amounts: { ...ts.amounts, deduction: used },
                    };
                  })
                  .filter((ts) => ts.amounts.total > 0 || ts.deduction > 0)
              : [];
        } catch (error) {
          console.error("error in ts calculations", error);
        }
        console.log("trigger number 6");
        dispatch(
          setCurrentPayrollEditing({
            // totalUnpaidPayAmount:value,
            unpaidtimesheets: unpaidTimesheetsUpdateForAdjustments || [],
            totalTimesheetPayAmount:
              currentPayrollEditing?.currentTimesheetPayAmount + value,
            modifications: { type: 1, amt: value },
          }),
        );
      } else if (formData.radioSelection == "2") {
        const netPay = calculateTotalAmount(currentPayrollEditing) || 0;
        const differenceWithLca =
          netPay -
          Number((currentPayrollEditing?.lcaAmount || 0).toFixed(2)) -
          Number((currentPayrollEditing?.totalUnpaidPayAmount || 0).toFixed(2));
        const difference =
          calculateTotalAmount(currentPayrollEditing) -
          Number((currentPayrollEditing?.lcaAmount || 0).toFixed(2));

        let finalDifferenceWithLca =
          differenceWithLca < 0 ? -differenceWithLca : differenceWithLca;

        if (
          currentPayrollEditing?.currentTimesheetPayAmount >
          finalDifferenceWithLca
        ) {
          showToast(
            "Total timesheet amount exceeds the LCA amount, match with LCA is not available",
            "info",
            10000,
          );
          return;
        }
        console.log({ difference, finalDifferenceWithLca });
        if (difference > 0) {
          handleChange("matchLca", finalDifferenceWithLca);

          let unpaidTimesheetsUpdateForAdjustments =
            currentPayrollEditing?.unpaidtimesheets || [];

          try {
            let deduction = finalDifferenceWithLca;
            unpaidTimesheetsUpdateForAdjustments =
              currentPayrollEditing?.unpaidtimesheets
                ? [...currentPayrollEditing.unpaidtimesheets]
                    .sort((a, b) => {
                      const totalA = Number(a.amounts?.total || 0);
                      const totalB = Number(b.amounts?.total || 0);
                      if (totalA !== totalB) return totalA - totalB;
                      return Number(a.uiid) - Number(b.uiid);
                    })
                    .map((ts) => {
                      let available = Number(ts.amounts?.total || 0);
                      let used = Math.min(available, deduction);
                      deduction -= used;
                      return {
                        ...ts,
                        amounts: { ...ts.amounts, deduction: used },
                      };
                    })
                    .filter((ts) => ts.amounts.total > 0 || ts.deduction > 0)
                : [];
          } catch (error) {
            console.error("error in ts calculations", error);
          }
          console.log("trigger number 7");
          dispatch(
            setCurrentPayrollEditing({
              unpaidtimesheets: unpaidTimesheetsUpdateForAdjustments || [],
              totalTimesheetPayAmount:
                currentPayrollEditing?.currentTimesheetPayAmount +
                finalDifferenceWithLca,
              modifications: { type: 2, amt: finalDifferenceWithLca },
            }),
          );
        } else if (difference < 0) {
          handleChange("matchLca", Math.abs(difference || 0));
          let unpaidTimesheetsUpdateForAdjustments =
            currentPayrollEditing?.unpaidtimesheets || [];

          try {
            let deduction = Math.abs(difference || 0);
            unpaidTimesheetsUpdateForAdjustments =
              currentPayrollEditing?.unpaidtimesheets
                ? [...currentPayrollEditing.unpaidtimesheets]
                    .sort((a, b) => {
                      const totalA = Number(a.amounts?.total || 0);
                      const totalB = Number(b.amounts?.total || 0);
                      if (totalA !== totalB) return totalA - totalB;
                      return Number(a.uiid) - Number(b.uiid);
                    })
                    .map((ts) => {
                      let available = Number(ts.amounts?.total || 0);
                      let used = Math.min(available, deduction);
                      deduction -= used;
                      return {
                        ...ts,
                        amounts: { ...ts.amounts, deduction: used },
                      };
                    })
                    .filter((ts) => ts.amounts.total > 0 || ts.deduction > 0)
                : [];
          } catch (error) {
            console.error("error in ts calculations", error);
          }
          console.log("trigger number 8");
          dispatch(
            setCurrentPayrollEditing({
              unpaidtimesheets: unpaidTimesheetsUpdateForAdjustments || [],
              totalTimesheetPayAmount:
                currentPayrollEditing?.currentTimesheetPayAmount +
                Math.abs(difference || 0),
              modifications: { type: 2, amt: Math.abs(difference || 0) },
            }),
          );
        }
      }
    }
  }, [formData?.radioSelection, formData?.enterValue, formData?.matchLca]);

  useEffect(() => {
    if (currentPayrollEditing?.modifications?.type === 1) {
      handleChange("enterValue", currentPayrollEditing.modifications.amt);
      handleChange(
        "radioSelection",
        String(currentPayrollEditing?.modifications?.type),
      );
    } else if (currentPayrollEditing?.modifications?.type === 2) {
      handleChange("matchLca", currentPayrollEditing.modifications.amt);
      handleChange(
        "radioSelection",
        String(currentPayrollEditing?.modifications?.type),
      );
    }
  }, [
    currentPayrollEditing?.modifications?.amt,
    currentPayrollEditing?.modifications?.type,
  ]);

  const handleClearEnterValue = useCallback(() => {
    const currentTimesheetPayAmount =
      currentPayrollEditing?.currentTimesheetPayAmount;
    const totalUnpaidPayAmount = currentPayrollEditing?.totalUnpaidPayAmount;
    const sum = currentTimesheetPayAmount + totalUnpaidPayAmount;
    handleChange("enterValue", 0);
    // dispatch(
    //   setCurrentPayrollEditing({
    //     ...currentPayrollEditing,
    //     totalTimesheetPayAmount:sum,
    //     modifications: { type: 1, amt: sum },
    //   })
    // );
    setShowUnpaid(true);
    console.log("trigger number 9");
    dispatch(
      setCurrentPayrollEditing({
        unpaidtimesheets: [],
        totalUnpaidTimesheetHours: 0,
        totalUnpaidTimesheetPayAmount: 0,
        totalUnpaidHours: 0,
        totalUnpaidPayAmount: 0,
        currentTimesheetHours:
          currentPayrollEditing?.currentTimesheetHours || 0,
        currentTimesheetPayAmount:
          currentPayrollEditing?.currentTimesheetPayAmount || 0,
        totalTimesheetHours: currentPayrollEditing?.currentTimesheetHours || 0,
        totalTimesheetPayAmount:
          (currentPayrollEditing?.modifications?.amt
            ? currentPayrollEditing?.modifications?.amt
            : currentPayrollEditing?.modifications?.amt === 0
              ? 0
              : 0) + (currentPayrollEditing?.totalTimesheetPayAmount || 0),
        modifications: { type: 1, amt: 0 },
      }),
    );
  }, [currentPayrollEditing]);

  return (
    <div className="px-8 py-6 ">
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
      <PageHeader
        RightSection={
          <div className="flex gap-3">
            <div className="min-w-42 lg:min-w-52">
              <InfoCardNewAdv
                layout="option5"
                title="Total Hours"
                titleConfig={{ className: "text-[16px]!" }}
                value={currentPayrollEditing?.totalTimesheetHours || 0}
                valueConfig={{ className: "text-[24px]! text-info!" }}
                layoutConfig={{
                  layoutClassName: "gap-2!",
                }}
                isLoading={payrollLoading}
              />
            </div>
            <div className="min-w-42 lg:min-w-52">
              <InfoCardNewAdv
                layout="option5"
                titleConfig={{ className: "text-[16px]!" }}
                title="Total Pay Amount"
                value={formatCurrency(
                  currentPayrollEditing?.totalTimesheetPayAmount,
                )}
                valueConfig={{ className: "text-[24px]! text-success!" }}
                layoutConfig={{
                  layoutClassName: "gap-2!",
                }}
                isLoading={payrollLoading}
              />
            </div>
          </div>
        }
      />
      <div>
        <div className="flex mt-6">
          <PageTitle
            title="Approved and Submitted timesheets during this pay cycle."
            tag="h6"
          />
        </div>
        <div className="flex gap-8 my-6">
          <KeyValue
            label={"Total Hours"}
            value={currentPayrollEditing?.currentTimesheetHours || 0}
            oneLine
          />

          <KeyValue
            label={"Total Pay Amount"}
            value={formatCurrency(
              currentPayrollEditing?.currentTimesheetPayAmount || 0,
            )}
          />
        </div>
        <div className={`${disableEditing ? "cursor-not-allowed z-100" : ""}`}>
          {!disableEditing ? (
            <NewTable
              onSelectionChange={handleSelectionChange}
              showCheckbox={true}
              defaultSelectedRows={
                currentPayrollEditing?.tabswitched
                  ? currentIds
                  : rowData?.timesheets?.ids
              }
              rows={mappedRows}
              columns={columns}
              hideToolBar
            />
          ) : (
            <NewTableAdv
              disableSelection={disableEditing}
              onSelectionChange={handleSelectionChange}
              showCheckbox={true}
              defaultSelectedRows={
                currentPayrollEditing?.tabswitched
                  ? currentIds
                  : rowData?.timesheets?.ids
              }
              rows={mappedRows}
              columns={columns}
              limit={100}
              hideToolBar
              toolbarRight={{
                search: false,
                filter: false,
                download: false,
              }}
            />
          )}
        </div>
        <div className="my-6">
          <h6>All Unpaid (Submitted & Approved) timesheets.</h6>
        </div>
        <div className="mt-4">
          <Button
            title={"Show All Unpaid (Submitted & Approved) Timesheets"}
            disabled={disableEditing}
            onClick={() => {
              setUnpaidTimesheetDetails(true);
              setShowUnpaid(false);
            }}
          />
        </div>

        <>
          <div className="flex gap-5 mt-4 items-center ">
            <InfoText
              value={currentPayrollEditing?.totalUnpaidHours || 0}
              title={"Total Hours"}
              oneLine
              color="black"
            />
            {currentPayrollEditing?.modifications?.amt > 0 && (
              <InfoText
                value={formatCurrency(
                  currentPayrollEditing?.modifications?.amt || 0,
                )}
                title={"Total Consumed"}
                oneLine
                color="black"
              />
            )}
            <InfoText
              value={formatCurrency(
                currentPayrollEditing?.totalUnpaidPayAmount || 0,
              )}
              title={"Total Amount"}
              oneLine
              color="black"
            />
            {(showUnpaid || hasUnpaidSelected) && (
              <>
                <div className="flex gap-1">
                  <FormFieldSet
                    type="radio"
                    title=" "
                    noTitle
                    options={[{ value: 1, label: "Enter Value" }]}
                    value={formData.radioSelection}
                    name="radioSelection"
                    onChange={(event) =>
                      handleChange("radioSelection", event.target.value)
                    }
                    disabled={disableEditing}
                  />
                  <FormFieldSet
                    type="text"
                    title=""
                    noTitle
                    placeholder="Enter Value"
                    size="sm"
                    value={formData.enterValue}
                    onChange={(value) => {
                      if (isNaN(value)) {
                        showToast("Please enter a valid number", "error", 3000);
                        return;
                      }
                      if (
                        Number(value) <=
                        currentPayrollEditing?.totalUnpaidPayAmount
                      ) {
                        handleChange("enterValue", value);
                      } else {
                        showToast(
                          "Amount should not exceed the total unpaid timesheet amount",
                          "warning",
                        );
                      }
                    }}
                    disabled={formData.radioSelection !== "1" || disableEditing}
                  />
                  {formData?.enterValue > 0 && (
                    <Button
                      type="icon"
                      variant="text"
                      icon="backspace"
                      size="lg"
                      color="danger"
                      onClick={handleClearEnterValue}
                      disabled={disableEditing}
                    />
                  )}
                </div>

                <div className="flex gap-1">
                  <FormFieldSet
                    type="radio"
                    title=" "
                    noTitle
                    options={[{ value: 2, label: "Match LCA Amount" }]}
                    value={formData.radioSelection}
                    name="radioSelection"
                    onChange={(event) =>
                      handleChange("radioSelection", event.target.value)
                    }
                    disabled={
                      !currentPayrollEditing?.timesheets?.length ||
                      calculateTotalAmount(currentPayrollEditing) >
                        currentPayrollEditing?.lcaAmount
                    }
                  />
                  <FormFieldSet
                    type="text"
                    title=""
                    noTitle
                    placeholder="Match LCA Amount"
                    size="sm"
                    value={formData.matchLca}
                    onChange={(value) => {
                      if (isNaN(value)) {
                        showToast("Please enter a valid number", "error", 3000);
                        return;
                      }
                      handleChange("matchLca", value);
                    }}
                    disabled={true}
                  />
                </div>
              </>
            )}
          </div>
          {(showUnpaid || hasUnpaidSelected) && (
            <div className="mt-3">
              <NewTableAdv
                rows={mappedRowsTwo}
                columns={columnsTwo}
                toolbarRight={{
                  search: false,
                  filter: false,
                  download: false,
                }}
              />
            </div>
          )}
        </>
      </div>

      <UnpaidTimesheetsTwo
        isOpen={unpaidTimesheetDetails}
        onClose={() => setUnpaidTimesheetDetails(false)}
        setShowUnpaid={setShowUnpaid}
        currentUnpaidIds={currentUnpaidIds}
        unpaidTimesheetDetailsData={unpaidTimesheetDetailsData}
        handleSelectionChangeUnpaid={handleSelectionChangeUnpaid}
        calculateRowDetailsWithRates={calculateRowDetailsWithRates}
      />
    </div>
  );
};

export default Timesheets;
